import { useCallback } from "react";

import { Box, Button, Group, Stack } from "@mantine/core";
import { DateInput, type DateValue, TimeInput } from "@mantine/dates";
import type { UseFormReturnType } from "@mantine/form";

import { IconCalendar, IconClock } from "@tabler/icons-react";
import { format, getHours, getMinutes, getSeconds, isValid, parseISO, set } from "date-fns";

import { TextWrapper } from "@/components/TextWrapper";
import { useStableCalendarDateInputState } from "@/intake-forms/hooks/useStableCalendarDateInputState";
import { useControlledTimeInput } from "@/utils/hooks/useControlledTimeInput";

/** Midnight on the local calendar date for the given reference instant. */
function getLocalCalendarDateAnchor(reference = new Date()): Date {
  return new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
}

/**
 * Merges a local `HH:mm:ss` time into a Date, anchoring to today when no date is set yet.
 * Returns `undefined` when the requested local time doesn't exist (a DST "spring forward" gap),
 * since `Date#setHours` silently shifts such times instead of rejecting them.
 */
function mergeLocalTimeIntoDate(
  currentValue: Date | null | undefined,
  display: string,
): Date | undefined {
  const dateSource =
    currentValue && isValid(currentValue) ? new Date(currentValue) : getLocalCalendarDateAnchor();
  const [hours, minutes, seconds] = display.split(":").map(Number);
  dateSource.setHours(hours, minutes, seconds, 0);
  if (
    dateSource.getHours() !== hours ||
    dateSource.getMinutes() !== minutes ||
    dateSource.getSeconds() !== seconds
  ) {
    return undefined;
  }
  return dateSource;
}

export interface DateTimePickerNowFieldProps<
  T extends Record<K, Date | null | undefined>,
  K extends keyof T & string,
> {
  form: UseFormReturnType<T>;
  name: K;
  label?: string;
  clearable?: boolean;
  /** Stable test id; the date input, time input, and Now button use `-date`/`-time`/`-now` suffixes. */
  dataTestId?: string;
}

/** A single dateTime field rendered as separate Date/Time inputs, paired with a Now button that stamps the current moment. */
export function DateTimePickerNowField<
  T extends Record<K, Date | null | undefined>,
  K extends keyof T & string,
>({ form, name, label, clearable = true, dataTestId }: DateTimePickerNowFieldProps<T, K>) {
  const currentValue = form.values[name] as Date | null | undefined;

  const setValue = useCallback(
    (value: Date | null) => {
      (form.setFieldValue as (path: K, value: Date | null) => void)(name, value);
    },
    [form, name],
  );

  const handleNowClick = () => {
    setValue(new Date());
  };

  const storedToDisplay = useCallback((value: Date) => format(value, "HH:mm:ss"), []);

  const displayToStored = useCallback(
    (display: string) => mergeLocalTimeIntoDate(currentValue, display),
    [currentValue],
  );

  const { displayValue, inputRef, onFocus, onBlur, onChange, openPicker } = useControlledTimeInput({
    storedValue: currentValue,
    onCommit: setValue,
    storedToDisplay,
    displayToStored,
    autoPopulate: false,
  });

  const { stableDateValue, calendarDate, handleCalendarDateChange } =
    useStableCalendarDateInputState(currentValue);

  const handleDateChange = (dateVal: DateValue) => {
    if (!dateVal) {
      setValue(null);
      return;
    }

    // Mantine v8 returns "YYYY-MM-DD" strings; parseISO treats them as local calendar
    // dates instead of UTC midnight from `new Date("YYYY-MM-DD")`.
    const selectedCalendarDate = typeof dateVal === "string" ? parseISO(dateVal) : dateVal;
    const currentDateTime = currentValue && isValid(currentValue) ? currentValue : new Date();

    const correctedDate = set(selectedCalendarDate, {
      hours: getHours(currentDateTime),
      minutes: getMinutes(currentDateTime),
      seconds: getSeconds(currentDateTime),
      milliseconds: 0,
    });

    // Reject rather than silently shift: the current time-of-day can land in a DST "spring
    // forward" gap on the newly selected date, which `set` resolves by bumping the hour.
    if (
      correctedDate.getHours() !== getHours(currentDateTime) ||
      correctedDate.getMinutes() !== getMinutes(currentDateTime) ||
      correctedDate.getSeconds() !== getSeconds(currentDateTime)
    ) {
      return;
    }

    setValue(correctedDate);
  };

  const timeInputLeftSection = (
    <Box
      component="span"
      style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
      onClick={(event) => {
        event.stopPropagation();
        openPicker();
      }}
    >
      <IconClock size={20} stroke={1.5} />
    </Box>
  );

  return (
    <Stack gap="xs">
      {label ? <TextWrapper variant="label">{label}</TextWrapper> : null}
      <Group align="flex-end">
        <DateInput
          label="Date"
          style={{ flex: 1 }}
          date={calendarDate}
          clearable={clearable}
          allowDeselect={false}
          value={stableDateValue}
          valueFormat="MM/DD/YYYY"
          placeholder="MM/DD/YYYY"
          onChange={handleDateChange}
          onDateChange={handleCalendarDateChange}
          leftSection={<IconCalendar size={20} />}
          data-testid={dataTestId ? `${dataTestId}-date` : undefined}
        />
        <TimeInput
          withSeconds
          label="Time"
          ref={inputRef}
          onBlur={onBlur}
          onFocus={onFocus}
          style={{ flex: 1 }}
          onChange={onChange}
          value={displayValue}
          leftSection={timeInputLeftSection}
          data-testid={dataTestId ? `${dataTestId}-time` : undefined}
        />
        <Button
          size="sm"
          variant="outline"
          color="burgundy.9"
          onClick={handleNowClick}
          data-testid={dataTestId ? `${dataTestId}-now` : undefined}
        >
          Now
        </Button>
      </Group>
    </Stack>
  );
}
